import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AppraisalCycle, Review } from "@/types/performance";
import { CalendarCheck, Star, CaretRight, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export function Appraisals() {
  const [cycles] = useKV<AppraisalCycle[]>("performance-cycles", [
    {
      id: "cycle-1",
      name: "Q1 2026 Performance Review",
      startDate: "2026-01-01",
      endDate: "2026-03-31",
      status: "Active",
      participants: []
    }
  ]);
  
  const [reviews, setReviews] = useKV<Review[]>("performance-reviews", [
    {
      id: "rev-1",
      cycleId: "cycle-1",
      employeeId: "CURRENT_USER",
      reviewerId: "CURRENT_USER",
      type: "Self",
      status: "Pending",
      ratings: {},
      comments: {}
    }
  ]);

  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [activeReview, setActiveReview] = useState<Review | null>(null);
  const [rating, setRating] = useState<string>("0");
  const [comment, setComment] = useState("");

  const handleStartReview = (review: Review) => {
    setActiveReview(review);
    setIsReviewOpen(true);
  };

  const handleSubmitReview = () => {
    if (!activeReview) return;
    
    // In a real app, save complex ratings map
    const updated = reviews.map(r => 
      r.id === activeReview.id 
        ? { ...r, status: 'Submitted', submittedAt: new Date().toISOString() } as Review
        : r
    );
    setReviews(updated);
    setIsReviewOpen(false);
    toast.success("Review submitted successfully");
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Active Cycles */}
        <Card>
          <CardHeader>
            <CardTitle>Active Cycles</CardTitle>
            <CardDescription>Performance reviews currently in progress</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {cycles.filter(c => c.status === 'Active').map(cycle => (
              <div key={cycle.id} className="border rounded-lg p-4 flex justify-between items-center bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/30">
                <div>
                  <h4 className="font-semibold">{cycle.name}</h4>
                  <p className="text-sm text-slate-500">Due by {new Date(cycle.endDate).toLocaleDateString()}</p>
                </div>
                <Badge>Active</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* My Tasks */}
        <Card>
          <CardHeader>
            <CardTitle>My Tasks</CardTitle>
            <CardDescription>Reviews pending your input</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {reviews.filter(r => r.status === 'Pending').map(review => (
              <div key={review.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-white dark:bg-slate-800 p-2 rounded-full shadow-sm">
                    <Star className="text-amber-500" weight="fill" />
                  </div>
                  <div>
                    <div className="font-medium">{review.type} Review</div>
                    <div className="text-xs text-slate-500">For Q1 2026 Cycle</div>
                  </div>
                </div>
                <Button size="sm" onClick={() => handleStartReview(review)}>
                  Start <CaretRight className="ml-1 w-3 h-3" />
                </Button>
              </div>
            ))}
            {reviews.filter(r => r.status === 'Pending').length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">All caught up! No pending reviews.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Review History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-500 text-center py-8">
            No past reviews available yet.
          </div>
        </CardContent>
      </Card>

      <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{activeReview?.type} Assessment</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            <div className="space-y-2">
              <Label>Overall Rating</Label>
              <Select value={rating} onValueChange={setRating}>
                <SelectTrigger><SelectValue placeholder="Select rating" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 - Exceeds Expectations</SelectItem>
                  <SelectItem value="4">4 - Meets Expectations</SelectItem>
                  <SelectItem value="3">3 - Needs Improvement</SelectItem>
                  <SelectItem value="2">2 - Unsatisfactory</SelectItem>
                  <SelectItem value="1">1 - Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>What went well?</Label>
              <Textarea placeholder="Highlight achievements..." className="h-24" />
            </div>

            <div className="space-y-2">
              <Label>Areas for improvement</Label>
              <Textarea placeholder="Constructive feedback..." className="h-24" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReviewOpen(false)}>Save Draft</Button>
            <Button onClick={handleSubmitReview}><Check className="mr-2" /> Submit Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
